import axios from "axios";
import { useEffect, useState } from "react";
import { Calendar2Week } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

export default function PublicAvailabilitySettings({
  publicAvailability,
  profile,
  token,
  onPublicAvailabilityUpdated,
}) {
  const [settings, setSettings] = useState(publicAvailability);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(createEmptyMessage);

  useEffect(() => {
    setSettings(publicAvailability);
  }, [publicAvailability]);

  const clearMessage = () => {
    setMessage(createEmptyMessage());
  };

  const getFullUrl = () => {
    const profileSlug =
      profile?.slug ??
      profile?.Slug ??
      "";

    const path =
      settings?.url ||
      `/availability/${settings?.slug || profileSlug}`;

    if (!path) {
      return "";
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const normalisedPath = path.startsWith("/")
      ? path
      : `/${path}`;

    return `${window.location.origin}${normalisedPath}`;
  };

  const togglePublicAvailability = async () => {
    try {
      setIsSaving(true);
      clearMessage();

      const nextValue = !settings.enabled;

      const response = await axios.put(
        `${ApiConnectionString}/api/therapist/settings/public-availability`,
        {
          publicAvailabilityEnabled: nextValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data || {};

      const updatedSettings = {
        enabled:
          data.publicAvailabilityEnabled ??
          data.PublicAvailabilityEnabled ??
          nextValue,
        slug:
          data.slug ??
          data.Slug ??
          settings.slug,
        url:
          data.publicAvailabilityUrl ??
          data.PublicAvailabilityUrl ??
          settings.url,
      };

      setSettings(updatedSettings);
      onPublicAvailabilityUpdated(updatedSettings);

      setMessage({
        error: "",
        success: updatedSettings.enabled
          ? "Public availability is now turned on."
          : "Public availability is now turned off.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to update public availability."
        ),
        success: "",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const copyLink = async () => {
    const fullUrl = getFullUrl();

    if (!fullUrl) {
      setMessage({
        error: "The public availability link is not available.",
        success: "",
      });

      return;
    }

    try {
      await navigator.clipboard.writeText(fullUrl);

      setMessage({
        error: "",
        success: "Public availability link copied.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: "Could not copy the link. Please copy it manually.",
        success: "",
      });
    }
  };

  return (
    <SettingsCard
      icon={<Calendar2Week />}
      title="Public availability"
      description="Let people view your general availability without logging in."
    >
      <div className="settings-form">
        <SectionMessage message={message} />

        <div className="settings-help-box">
          This only shows general availability ranges. It does not show
          client names, private appointments, notes or diary details.
        </div>

        <div>
          <label>Status</label>

          <div className="settings-readonly-value">
            {settings.enabled
              ? "Public link is on"
              : "Public link is off"}
          </div>

          <small>
            {settings.enabled
              ? "People with the link can view your general availability."
              : "Your public availability page is currently hidden."}
          </small>
        </div>

        {settings.enabled && (
          <div>
            <label>Public availability link</label>

            <div className="settings-readonly-value settings-link-value">
              {getFullUrl()}
            </div>

            <small>
              Share this link with people who want to view availability.
            </small>
          </div>
        )}

        <div className="settings-actions">
          <button
            type="button"
            onClick={togglePublicAvailability}
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : settings.enabled
                ? "Turn off public availability"
                : "Turn on public availability"}
          </button>

          {settings.enabled && (
            <button
              type="button"
              onClick={copyLink}
              disabled={isSaving}
            >
              Copy link
            </button>
          )}
        </div>
      </div>
    </SettingsCard>
  );
}